import { useTerminalsState } from "@/States/Terminals";
import { useGlobalAlert } from "@/States/globalAlert";
import { trpc } from "@/Utils/trpc";
import { useTrans } from "@/translations";

import { useEffect, useMemo } from "react";

import { isEqual, uniq } from "lodash";
import { create } from "zustand";
import { combine } from "zustand/middleware";

export type WasteTypeConfig = {
  wasteTypeCode: string;
  color?: string | null;
  name?: string;
  handledBySmartInfra?: boolean;
  downstreamHandlingId?: string | null;
  co2factor?: number | null;
  currentCost?: {
    id: string;
    costPerUnit: number;
    validFrom: Date;
    validTo: Date | null;
  };
};

export type WasteTypeData = {
  wasteTypeCode: string;
  names: {
    displayName?: string;
    locale: string;
  }[];
  color?: string | null;
  downstreamHandlingId?: string | null;
  downstreamHandlingPlaceholderId?: string;
  co2factor?: number | null;
  co2Placeholder?: string;
  costs?: {
    costPerUnit: number | string;
    validFrom: Date;
    validTo: Date | null;
  }[];
};

export const wasteTypeConfigStore = create<{
  wasteTypeConfig: WasteTypeConfig[];
  setWasteTypeConfig: (wasteTypeConfig: WasteTypeConfig[]) => void;
}>(
  combine(
    {
      wasteTypeConfig: [],
    } as any,
    (set) =>
      ({
        setWasteTypeConfig: (wasteTypeConfig: WasteTypeConfig[]) =>
          set({ wasteTypeConfig }),
      }) as any,
  ),
);

export const useConfigService = (terminalIdOverride?: string) => {
  const { language } = useTrans();
  const { setGlobalAlert } = useGlobalAlert();
  const { currentTerminal } = useTerminalsState();
  const { wasteTypeConfig, setWasteTypeConfig } = wasteTypeConfigStore();

  const { data: clientWasteTypeConfig, refetch: refetchClientWasteTypeConfig } =
    trpc.config.getClientWasteTypeConfig.useQuery();

  const safeClientWasteTypeConfig = useMemo(
    () => (Array.isArray(clientWasteTypeConfig) ? clientWasteTypeConfig : []),
    [clientWasteTypeConfig],
  );

  const terminalId = terminalIdOverride || currentTerminal?.id;

  const {
    data: terminalWasteTypeConfig = [],
    refetch: refetchTerminalWasteTypeConfig,
    isLoading: isLoadingTerminalWasteTypeConifg,
  } = trpc.config.getTerminalWasteTypeConfig.useQuery(
    { terminalId: terminalId! },
    {
      enabled: !!terminalId,
      select: (data) =>
        data.map((el) => ({
          ...el,
          name: el.names.find((n) => n.locale === language)?.displayName,
          currentCost: el.costs.find((c) => !c.validTo),
        })),
    },
  );

  const {
    data: terminalConfig,
    refetch: refetchTerminalConfig,
    isLoading: isLoadingTerminalConfig,
  } = trpc.config.getTerminalConfig.useQuery(
    { terminalId: terminalId! },
    { enabled: !!terminalId },
  );

  const { mutate: updateTerminalConfig } =
    trpc.config.updateTerminalConfig.useMutation({
      onSuccess: () => {
        refetchTerminalConfig();
        setGlobalAlert({
          type: "success",
          message: "systemMessages:changesSaved",
        });
      },
      onError: (error) => {
        setGlobalAlert({
          type: "error",
          message: "errors:failedSave",
          instructions: error.message,
        });
      },
    });

  const localizedClientConfig = useMemo(
    () =>
      safeClientWasteTypeConfig.map((el) => ({
        wasteTypeCode: el.wasteTypeCode,
        color: el.color,
        name: el.names.find((n) => n.locale === language)?.displayName,
        downstreamHandlingId: el.downstreamHandlingId,
        co2factor: el.co2factor,
      })),
    [safeClientWasteTypeConfig, language],
  );

  const unifiedLocalizedConfig: WasteTypeConfig[] = useMemo(() => {
    const wasteTypesWithConfig = uniq([
      ...localizedClientConfig.map((el) => el.wasteTypeCode),
      ...terminalWasteTypeConfig.map((el) => el.wasteTypeCode),
    ]);

    const unifiedConfig = wasteTypesWithConfig.map((wasteTypeCode) => {
      const clientConfig = localizedClientConfig.find(
        (el) => el.wasteTypeCode === wasteTypeCode,
      );
      const twtc = terminalWasteTypeConfig.find(
        (el) => el.wasteTypeCode === wasteTypeCode,
      );

      return {
        wasteTypeCode,
        color: twtc?.color || clientConfig?.color || "",
        name: twtc?.name || clientConfig?.name,
        handledBySmartInfra: twtc?.handledBySmartInfra,
        downstreamHandlingId:
          twtc?.downstreamHandlingId ||
          clientConfig?.downstreamHandlingId ||
          undefined,
        co2factor:
          Number(twtc?.co2factor || clientConfig?.co2factor) || undefined,
      };
    });

    return unifiedConfig;
  }, [localizedClientConfig, terminalWasteTypeConfig]);

  useEffect(() => {
    // If terminalIdOverride is not provided, we are in global config. Fixes infinite loop issue.
    if (!terminalIdOverride)
      if (!isEqual(wasteTypeConfig, unifiedLocalizedConfig)) {
        setWasteTypeConfig(unifiedLocalizedConfig);
      }
  }, [
    unifiedLocalizedConfig,
    setWasteTypeConfig,
    wasteTypeConfig,
    terminalIdOverride,
  ]);

  return {
    wasteTypeClientConfig: localizedClientConfig,
    wasteTypeConfig: unifiedLocalizedConfig,
    clientWasteTypeConfig: safeClientWasteTypeConfig,
    terminalWasteTypeConfig,
    refetchClientWasteTypeConfig,
    refetchTerminalWasteTypeConfig,
    terminalConfig,
    refetchTerminalConfig,
    updateTerminalConfig,
    isLoadingTerminalConfig,
    isLoadingTerminalWasteTypeConifg,
  };
};
